import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';

const ThanksPage = () => {
  const { t, language } = useTranslations();
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Support both 'session_id' and 'sid' parameters for compatibility
  const sessionId = searchParams.get('session_id') || searchParams.get('sid');
  
  useEffect(() => {
    if (sessionId) {
      // Poll payment status with timeout
      const timeoutId = setTimeout(() => {
        if (loading) {
          console.warn('Payment verification timeout, showing fallback UI');
          setLoading(false);
          setError(null);
          // Set a partial payment data to show processing state
          setPaymentData({
            success: true,
            payment_status: 'processing',
            session_id: sessionId
          });
        }
      }, 8000); // 8 second timeout

      checkPaymentStatus(sessionId);
      
      return () => clearTimeout(timeoutId);
    } else {
      setLoading(false);
      setError(language === 'es' ? 'No se proporcionó ID de sesión' : 'No session ID provided');
    }
  }, [sessionId, loading, language]);
  
  const checkPaymentStatus = async (sid) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl) {
        throw new Error('Backend URL not configured');
      }
      
      const response = await fetch(`${backendUrl}/api/stripe/checkout/status/${sid}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 6000 // 6 second timeout
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to check payment status'}`);
      }
      
      const data = await response.json();
      
      // Validate response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format');
      }
      
      setPaymentData(data);
      
      // Track GA4 purchase event for successful payments
      if (data.success && data.payment_status === 'paid' && window.gtag) {
        try {
          window.gtag('event', 'purchase', {
            transaction_id: sid,
            value: data.amount_total ? (data.amount_total / 100) : 0, // Convert from cents
            currency: data.currency || 'EUR',
            items: [{
              item_id: data.metadata?.package_id || 'unknown',
              item_name: data.metadata?.package_name || 'Unknown Product',
              price: data.amount_total ? (data.amount_total / 100) : 0,
              quantity: 1
            }]
          });
        } catch (gaError) {
          console.warn('GA4 tracking failed:', gaError);
        }
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      const errorMessage = err.message || 'Unknown error occurred';
      setError(language === 'es' ? 
        `Error al verificar el pago: ${errorMessage}` : 
        `Error verifying payment: ${errorMessage}`
      );
    } finally {
      setLoading(false);
    }
  };
  
  const getProductTypeFromMetadata = () => {
    if (!paymentData?.metadata) return 'unknown';
    const packageId = paymentData.metadata.package_id;
    
    if (packageId === 'professional_monthly' || packageId === 'professional_yearly') {
      return 'subscription';
    } else if (packageId === 'auditoria_marca' || packageId === 'estrategia_crecimiento' || 
               packageId === 'sesiones_individuales' || packageId === 'informe_unico') {
      return 'service';
    }
    return 'unknown';
  };
  
  const renderSuccessContent = () => {
    const productType = getProductTypeFromMetadata();
    
    if (productType === 'subscription') {
      return (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            {language === 'es' ? 
              'Tu suscripción está activa. Ya puedes acceder a la plataforma.' :
              'Your subscription is active. You can now access the platform.'
            }
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300"
            >
              {language === 'es' ? 'Entrar' : 'Log in'}
            </Link>
          </div>
        </div>
      );
    } else if (productType === 'service') {
      return (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            {language === 'es' ? 
              'En 48 horas recibirás un email con el formulario de inicio y fechas disponibles.' :
              'You will receive an email within 48 hours with the onboarding form and available dates.'
            }
          </h2>
          <div className="bg-slate-700/50 rounded-lg p-6 mb-6">
            <p className="text-gray-300">
              {language === 'es' ? 
                'Nuestro equipo se pondrá en contacto contigo muy pronto para coordinar los siguientes pasos.' :
                'Our team will contact you very soon to coordinate the next steps.'
              }
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            {language === 'es' ? 
              'Procesaremos tu pedido y te enviaremos los detalles por email.' :
              'We will process your order and send you the details by email.'
            }
          </h2>
        </div>
      );
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Simple navigation header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-white/20 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-xl font-bold text-white hover:scale-105 transition-transform duration-300">
              Your<span className="text-purple-400">Brand</span>Value
            </Link>
          </div>
        </div>
      </nav>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600/20 rounded-full mb-6">
            <span className="text-4xl">✅</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-6">
            {language === 'es' ? 'Pago confirmado' : 'Payment confirmed'}
          </h1>
          
          {loading ? (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                <span className="text-gray-300">
                  {language === 'es' ? 'Verificando pago...' : 'Verifying payment...'}
                </span>
              </div>
            </div>
          ) : error ? (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-red-500/20">
              <div className="text-red-400 mb-4">
                <span className="text-2xl mr-2">⚠️</span>
                {language === 'es' ? 'Error verificando el pago' : 'Error verifying payment'}
              </div>
              <p className="text-gray-300 mb-6">{error}</p>
              <Link
                to="/pricing"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                {language === 'es' ? 'Volver' : 'Go back'}
              </Link>
            </div>
          ) : paymentData?.success && paymentData?.payment_status === 'paid' ? (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-green-500/20">
              <div className="text-green-400 mb-6">
                <span className="text-6xl mb-4 block">🎉</span>
                <p className="text-xl">
                  {language === 'es' ? '¡Gracias por tu compra!' : 'Thank you for your purchase!'}
                </p>
              </div>
              
              {renderSuccessContent()}
              
              <div className="flex justify-center mt-8">
                <Link
                  to="/"
                  className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                >
                  {language === 'es' ? 'Volver a la web' : 'Back to site'}
                </Link>
              </div>
            </div>
          ) : paymentData?.success && (paymentData?.payment_status === 'processing' || paymentData?.payment_status === 'unpaid') ? (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-yellow-500/20">
              <div className="text-yellow-400 mb-6">
                <span className="text-2xl mr-2">⏳</span>
                {language === 'es' ? 'Procesando pago...' : 'Processing payment...'}
              </div>
              <p className="text-gray-300 mb-6">
                {language === 'es' ? 
                  'Tu pago se está procesando. Te enviaremos la confirmación por email en breve.' :
                  'Your payment is being processed. We will send you confirmation by email shortly.'
                }
              </p>
              <div className="flex justify-center">
                <Link
                  to="/"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                >
                  {language === 'es' ? 'Volver al inicio' : 'Return home'}
                </Link>
              </div>
            </div>
          ) : paymentData ? (
            /* Generic fallback for any other payment status */
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-blue-500/20">
              <div className="text-blue-400 mb-6">
                <span className="text-2xl mr-2">ℹ️</span>
                {language === 'es' ? 'Información del pago' : 'Payment information'}
              </div>
              <p className="text-gray-300 mb-6">
                {language === 'es' ? 
                  'Hemos recibido tu solicitud de pago. Te contactaremos pronto con los detalles.' :
                  'We have received your payment request. We will contact you soon with the details.'
                }
              </p>
              <div className="flex justify-center">
                <Link
                  to="/"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                >
                  {language === 'es' ? 'Volver al inicio' : 'Return home'}
                </Link>
              </div>
            </div>
          ) : (
            /* Ultimate fallback - should never reach here but prevents blank page */
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 border border-gray-500/20">
              <div className="text-gray-400 mb-6">
                <span className="text-2xl mr-2">⚠️</span>
                {language === 'es' ? 'Estado desconocido' : 'Unknown status'}
              </div>
              <p className="text-gray-300 mb-6">
                {language === 'es' ? 
                  'No pudimos verificar el estado del pago. Por favor, contacta con soporte si necesitas ayuda.' :
                  'We could not verify the payment status. Please contact support if you need assistance.'
                }
              </p>
              <div className="flex justify-center">
                <Link
                  to="/"
                  className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                >
                  {language === 'es' ? 'Volver al inicio' : 'Return home'}
                </Link>
              </div>
            </div>
          )}
        </div>
        
        {/* Debug information (only in development or when explicitly enabled) */}
        {(process.env.NODE_ENV === 'development' || process.env.REACT_APP_SHOW_DEBUG === 'true') && sessionId && (
          <div className="text-center mt-8">
            <details className="bg-slate-800/30 rounded-lg p-4 text-left max-w-md mx-auto">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                Debug Info (Session ID: {sessionId})
              </summary>
              <div className="mt-2 text-xs text-gray-400 space-y-1">
                <div>Environment: {process.env.NODE_ENV}</div>
                <div>Language: {language}</div>
                <div>Backend URL: {process.env.REACT_APP_BACKEND_URL || 'Not configured'}</div>
                {paymentData && (
                  <div>Payment Status: {paymentData.payment_status || 'Unknown'}</div>
                )}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThanksPage;